import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DialogComponentService } from '@merchant-portal/app/components/dialog/dialog.component.service';
import { BlockLoadingComponent } from '@merchant-portal/app/components/loading/block-loading/block-loading.component';
import { CodeInputComponent, CodeInputModule } from 'angular-code-input';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { take } from 'rxjs/operators';
import { InputModule } from 'ui-component/src/public-api';
import { TotpStatus } from '../../constants/totp-status.constants';
import { IUserInfo } from '../../models/login/login.interface';
import { AuthService } from '../auth.service';
import { OTP_EVENT_TYPE } from './constant/event.type';
import { OtpStoreFactory } from './factory/otp-store.factory';

@Component({
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss'],
  imports: [
    CommonModule,
    CodeInputModule,
    CardModule,
    InputTextModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    BlockLoadingComponent,
    DialogModule,
    ToastModule,
    InputModule,
  ],
})
export class OtpComponent implements OnChanges {
  @Input() mode: 'dialog' | 'page' | 'inline' = 'page'; // Default mode is 'page'
  @Input() displayOtpDialog = false;
  @Input() countDownStarted = false;
  @Input() resendDelaySeconds?: number;
  @Input() event: string = 'user-login';
  @Input() twoFactorAuthMethod: 'OTP' | 'TOTP' | string | null | undefined = null; // Add this input
  @Output() dialogChange = new EventEmitter();
  @Output() isOtpComplete = new EventEmitter();
  @Output() loginSuccess = new EventEmitter<IUserInfo>();
  @ViewChild('codeInput') codeInput!: CodeInputComponent;

  vm$ = this.otpStore.vm$;
  email = '';
  showVerificationMethodDialog = false;
  isTotpMode = false;

  // Authentication code dialog properties (for ACTIVE TOTP users)
  showAuthenticationCodeDialog = false;
  authCodeError = '';

  constructor(
    private otpStore: OtpStoreFactory,
    private authService: AuthService,
    private dialogComponentService: DialogComponentService
  ) {
    this.vm$.subscribe(state => {
      if (state.errorMessage) {
        if (this.isTotpMode || this.showAuthenticationCodeDialog) {
          // For TOTP mode, show error in auth code dialog
          this.authCodeError = state.errorMessage;
        } else {
          this.codeInput.reset();
        }
      }
      if (state.isAccountBlocked == true) {
        this.displayOtpDialog = false;
      }
      this.email = this.otpStore.getEmail();
      if (state.isEventFromInvitation) {
        this.isOtpComplete.emit(true);
      }

      if (!state.isLoading && !state.errorMessage && state.isVeritifyOtpSuccess) {
        if (this.isTotpMode || this.showAuthenticationCodeDialog) {
          // For reset pin with TOTP, handle differently - save token and emit completion
          if (this.event === OTP_EVENT_TYPE.FORGOT_PIN) {
            this.closeAuthenticationCodeDialog();
            this.isOtpComplete.emit(true);
            return;
          }

          // For TOTP mode, immediately hide the dialog to prevent showing OTP dialog
          if (this.event === 'user-login') {
            this.displayOtpDialog = false;
            this.dialogChange.emit(false);
          }

          // For TOTP mode, update user status and handle success
          const userInfo = this.authService.getUserInfo();
          if (userInfo) {
            userInfo.totpStatus = TotpStatus.ACTIVE;
            this.authService.setUserInfo(userInfo);
          }

          this.closeAuthenticationCodeDialog();
          this.handleSuccessfulTOTPVerification();
        } else {
          const userInfo = this.authService.getUserInfo();
          if (userInfo) {
            this.loginSuccess.emit(userInfo);
          }
        }
      }
    });
  }

  ngOnChanges() {
    if (this.resendDelaySeconds) {
      this.otpStore.setResendDelaySeconds(this.resendDelaySeconds);
    }

    // Check if this is TOTP mode from twoFactorAuthMethod input
    this.isTotpMode = this.twoFactorAuthMethod === 'TOTP';

    if (this.displayOtpDialog) {
      if (this.isTotpMode) {
        // For TOTP mode, show authentication code dialog directly
        this.authCodeError = '';
        this.showAuthenticationCodeDialog = true;
      } else {
        // For OTP mode (non-TOTP), check if there's an active session to resume
        const hasActiveSession = this.otpStore.hasActiveSession();

        if (hasActiveSession) {
          // Resume existing OTP session with countdown
          this.otpStore.loadOtpSession();
        } else if (this.countDownStarted) {
          // Start new countdown if explicitly requested
          this.otpStore.startCountdown();
        }
        // Note: Don't automatically start countdown if no active session and countDownStarted is false
        // This prevents making unnecessary API calls when dialog is reopened
      }
    } else if (this.mode === 'inline' && this.countDownStarted && !this.isTotpMode) {
      // Handle inline mode countdown for OTP
      const hasActiveSession = this.otpStore.hasActiveSession();
      if (hasActiveSession) {
        this.otpStore.loadOtpSession();
      } else {
        this.otpStore.startCountdown();
      }
      this.countDownStarted = true;
    }

    if (this.event == 'first-time-login') {
      this.resendOtpCode();
    }
  }

  onCodeChanged(otp: string) {
    this.otpStore.verifyOtp({ otp, event: this.event });
  }

  onDialogHide() {
    this.displayOtpDialog = false;

    // Only reset state and clear session if countdown is 0
    this.vm$.pipe(take(1)).subscribe((vm: any) => {
      if (vm.countdown === 0 || !vm.isCountdownActive) {
        this.otpStore.resetState();
        this.otpStore.clearOtpSession();
      }
      // If countdown is still active, keep the session for resuming later
    });

    this.codeInput.reset();
    this.dialogChange.emit(this.displayOtpDialog);
  }

  resendOtpCode() {
    this.otpStore.resendOtp({ event: this.event });
  }

  formatCountdown(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  getResendMessage(resendDelaySeconds?: number): string {
    const delayMinutes = Math.floor((resendDelaySeconds || 180) / 60);
    // The OTP is only sent when the email is registered, the response does not tell whether it is
    if (this.event === OTP_EVENT_TYPE.FORGOT_PASSWORD) {
      return `If <strong>${this.email}</strong> is registered, we've sent a 6-digit code to it. Check your inbox or spam, and click 'Resend Code' if not received in ${delayMinutes} minutes.`;
    }
    return `Enter the 6-digit code sent to <strong>${this.email}</strong>. Check your inbox or spam, and click 'Resend Code' if not received in ${delayMinutes} minutes.`;
  }

  showTryAnotherMethod(): boolean {
    // Show "Try Another Method" for login, change password, and reset pin
    const isValidEvent = [
      OTP_EVENT_TYPE.USER_LOGIN,
      OTP_EVENT_TYPE.CHANGE_PASSWORD,
      OTP_EVENT_TYPE.FORGOT_PIN,
    ].includes(this.event as OTP_EVENT_TYPE);

    if (!isValidEvent) {
      return false;
    }

    const userInfo = this.authService.getUserInfo();

    const isTOTPActive =
      userInfo?.isTOTPActive === true || userInfo?.totpStatus === TotpStatus.ACTIVE;

    // Show "Try Another Method" if user has TOTP active
    return isTOTPActive;
  }

  openVerificationMethodDialog() {
    this.showVerificationMethodDialog = true;
  }

  closeVerificationMethodDialog() {
    this.showVerificationMethodDialog = false;
  }

  selectOTPMethod() {
    this.closeVerificationMethodDialog();

    // Switch from TOTP to OTP mode - need to request a new OTP
    this.isTotpMode = false;
    this.twoFactorAuthMethod = 'OTP'; // Update the input property to reflect OTP mode

    // Clear any TOTP-related dialogs
    this.closeAuthenticationCodeDialog();

    // Request new OTP with explicit OTP method
    this.otpStore.resendOtp({ event: this.event, twoFactorAuthMethod: 'OTP' });

    // Start countdown for the new OTP
    this.otpStore.startCountdown();
  }

  selectAuthenticatorMethod() {
    this.closeVerificationMethodDialog();

    // Switch back to TOTP mode
    this.isTotpMode = true;
    this.twoFactorAuthMethod = 'TOTP';

    const userInfo: IUserInfo | null = this.authService.getUserInfo();
    const hasTOTPActive =
      userInfo?.isTOTPActive === true || userInfo?.totpStatus === TotpStatus.ACTIVE;

    if (hasTOTPActive) {
      // User has TOTP active - make API call with TOTP method and show auth code dialog
      this.otpStore.resendOtp({ event: this.event, twoFactorAuthMethod: 'TOTP' });
      this.authCodeError = '';
      this.showAuthenticationCodeDialog = true;
    } else {
      // For reset-pin and change-password, don't allow TOTP setup - just switch to OTP
      if (
        this.event === OTP_EVENT_TYPE.FORGOT_PIN ||
        this.event === OTP_EVENT_TYPE.CHANGE_PASSWORD
      ) {
        this.selectOTPMethod();
        return;
      }
    }
  }

  openGlobalAuthenticatorSetupDialogWithData(enrollmentData: any) {
    try {
      const setupDialogRef =
        this.dialogComponentService.openAuthenticatorSetupDialog(enrollmentData);

      // Handle the setup dialog result
      setupDialogRef.onClose.subscribe((result: any) => {
        if (result?.action === 'verified') {
          // Authenticator setup completed successfully
          if (this.event === OTP_EVENT_TYPE.FORGOT_PIN) {
            // For reset pin, after TOTP setup, we need to verify with TOTP code to get token
            this.handleResetPinTOTPFlow();
          } else if (this.event === OTP_EVENT_TYPE.CHANGE_PASSWORD) {
            // For change password, after TOTP setup, just emit completion (no additional verification needed)
            this.isOtpComplete.emit(true);
          } else {
            // For other events, just emit completion
            this.isOtpComplete.emit(true);
          }
        } else if (result?.action === 'cancel') {
          // Authenticator setup was cancelled
          if (
            this.event === OTP_EVENT_TYPE.FORGOT_PIN ||
            this.event === OTP_EVENT_TYPE.CHANGE_PASSWORD
          ) {
            // For reset-pin and change-password, just switch to OTP instead of reopening method dialog
            this.selectOTPMethod();
          } else {
            // For other events, reopen the verification method dialog
            this.openVerificationMethodDialog();
          }
        } else if (result?.action === 'totp_already_active') {
          // TOTP is already active, show authentication code dialog
          this.authCodeError = '';
          this.showAuthenticationCodeDialog = true;
        }
      });
    } catch (error) {
      console.error('Error opening authenticator setup dialog:', error);
    }
  }

  // Authentication code dialog methods (for ACTIVE TOTP users)
  closeAuthenticationCodeDialog() {
    this.showAuthenticationCodeDialog = false;
    this.authCodeError = '';
  }

  onAuthCodeChanged(code: string) {
    // Clear any previous errors
    this.authCodeError = '';

    // Handle the 6-digit authentication code input
    if (code.length === 6) {
      // Auto-verify when 6 digits are entered
      this.verifyAuthenticationCode(code);
    }
  }

  verifyAuthenticationCode(code: string) {
    // Use the standard OTP verification flow for TOTP
    this.otpStore.verifyOtp({ otp: code, event: this.event });
  }

  private handleSuccessfulTOTPVerification() {
    switch (this.event) {
      case OTP_EVENT_TYPE.CHANGE_PASSWORD:
        // For change password, emit completion
        this.isOtpComplete.emit(true);
        break;
      case OTP_EVENT_TYPE.FORGOT_PIN:
        // For reset pin, after TOTP setup, we need to get auth code and verify
        this.handleResetPinTOTPFlow();
        break;
      default:
        // Default behavior - emit completion
        this.isOtpComplete.emit(true);
        break;
    }
  }

  openVerificationMethodDialogFromAuth() {
    this.closeAuthenticationCodeDialog();
    this.openVerificationMethodDialog();
  }

  private handleResetPinTOTPFlow() {
    // For reset pin, after successful TOTP setup, we need to send TOTP request and verify with auth code
    this.otpStore.resendOtp({ event: this.event, twoFactorAuthMethod: 'TOTP' });

    // Show authentication code dialog for TOTP verification
    this.authCodeError = '';
    this.showAuthenticationCodeDialog = true;
  }
}
